/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { DropdownMenuItemType, IDropdownOption, IDropdownStyleProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/utilities';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CollapsedErrorMessage } from 'src/core/components/CollapsedErrorMessage';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TranslatorLanguage } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { ResourceValidationResult, validateTranslatorResource } from 'src/languageStudio/core/utils/demosUtils';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';

export type TranslatorDemosConfigsProps = {
    categories?: string[];
    showCategory?: boolean;
    sourceLanguageKey: string;
    selectedCategory?: string;
    storageAccountsError?: Error;
    targetLanguageKeys: string[];
    needStorageAccount?: boolean;
    selectedStorageAccount?: string;
    isStorageAccountsLoading?: boolean;
    refreshStorageAccounts?: () => void;
    isMultiTargetLanguageAvailable?: boolean;
    supportedLanguages: TranslatorLanguage[];
    storageAccounts?: AzureStorageAccountStore[];
    onCategoryChange?: (newCategory: string) => void;
    onSourceLanguageChange: (newKey: string) => void;
    onTargetLanguageChange: (newKeys: string[]) => void;
    onStorageAccountChange?: (storageAccount: string) => void;
};

const messages = defineMessages({
    createOne: { id: 'languageStudio.translatorDemosConfigs.createOne', defaultMessage: 'Create one' },
    targetLanguageLabel: { id: 'languageStudio.translatorDemosConfigs.targetLanguageLabel', defaultMessage: 'Translate to' },
    enableIdentity: { id: 'languageStudio.translatorDemosConfigs.enableIdentity', defaultMessage: 'Enable managed identity' },
    sourceLanguageLabel: { id: 'languageStudio.translatorDemosConfigs.sourceLanguageLabel', defaultMessage: 'Translate from' },
    autoDetectLanguageItem: { id: 'languageStudio.translatorDemosConfigs.autoDetectLanguageItem', defaultMessage: 'Auto-detect language' },
    selectLanguagePlaceholder: {
        id: 'languageStudio.translatorDemosConfigs.selectLanguagePlaceholder',
        defaultMessage: 'Select a language'
    },
    selectResourcePlaceholder: {
        id: 'languageStudio.translatorDemosConfigs.selectResourcePlaceholder',
        defaultMessage: 'Select a translator resource'
    },
    selectResourceLabel: {
        id: 'languageStudio.translatorDemosConfigs.selectResourceLabel',
        defaultMessage: 'Select your Translator resource (S1 or above)'
    },
    selectStorageAccountLabel: {
        id: 'languageStudio.translatorDemosConfigs.selectStorageAccountLabel',
        defaultMessage: 'Select Azure blob storage account'
    },
    selectStorageAccountPlaceholder: {
        id: 'languageStudio.translatorDemosConfigs.selectStorageAccountPlaceholder',
        defaultMessage: 'Select a blob storage account'
    },
    selectCategoryLabel: {
        id: 'languageStudio.translatorDemosConfigs.selectCategoryLabel',
        defaultMessage: 'Category (optional)'
    },
    selectCategoryPlaceholder: {
        id: 'languageStudio.translatorDemosConfigs.selectCategoryPlaceholder',
        defaultMessage: 'Select a category'
    },
    invalidPermissionsStorageAccount: {
        id: 'languageStudio.translatorDemosConfigs.invalidPermissionsStorageAccount',
        defaultMessage:
            'Your translator resource must have the "Owner" role and the user must have the "Storage Blob Data Owner" role in the Azure Blob storage account'
    },
    enableCORSFailed: {
        id: 'languageStudio.translatorDemosConfigs.enableCORSFailed',
        defaultMessage: `The portal cannot access your storage account "{storageAccountName}",
        please enable CORS policies on your storage account.`
    },
    invalidResource: {
        id: 'languageStudio.translatorDemosConfigs.invalidResource',
        defaultMessage: 'You must enable managed identity to connect a storage account.'
    }
});

const createButtonStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({ root: { fontSize: '14px' } });

const demoTheme = getScopedTheme('DemoPage');
const { styleComponent } = getStylist('DemoPage');
const SecondaryText = styleComponent<LuisTextProps>(LuisText)('SecondaryText', theme => ({
    color: theme.secondaryTextColor,
    marginTop: '5px'
}));

const languageDropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({ root: { flex: 1 } });
const dropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({ dropdown: { minWidth: '300px' } });
const calloutStyle = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: '300px !important' }
});

export const TranslatorDemosConfigs = observer((props: TranslatorDemosConfigsProps) => {
    const {
        categories = [],
        selectedCategory,
        onCategoryChange,
        sourceLanguageKey,
        targetLanguageKeys,
        supportedLanguages,
        storageAccountsError,
        storageAccounts = [],
        showCategory = false,
        onTargetLanguageChange,
        onSourceLanguageChange,
        refreshStorageAccounts,
        selectedStorageAccount,
        onStorageAccountChange,
        isStorageAccountsLoading,
        needStorageAccount = false,
        isMultiTargetLanguageAvailable = false
    } = props;
    const { localize } = useLocalization();
    const resourceTracker = useProgressTracker();
    const corsCheckTracker = useProgressTracker();
    const { dispatcher, rootStore } = useGlobalState();
    const storageAccountPermissionsTracker = useProgressTracker({ persistOnComplete: true });
    const [isValidResource, setIsValidResource] = React.useState<ResourceValidationResult>({ isValid: true });
    const resourcesStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);

    const { subscriptionId, csAccountStore } = rootStore.azureStore.session;
    const storageAccount = storageAccounts.find(s => s.name === selectedStorageAccount);
    const storageAccountsOptions: IDropdownOption[] = storageAccounts.map(c => ({ key: c.name, text: c.name }));
    const translatorResourcesOptions: IDropdownOption[] = resourcesStoreHouse.items.map(r => ({ data: r, key: r.id, text: r.name }));

    React.useEffect(() => {
        if (csAccountStore?.id) {
            setIsValidResource(validateTranslatorResource({ localize, resource: csAccountStore }));
        }
    }, [csAccountStore?.id]);

    const getLanguageOptions = (isTarget: boolean = false) => [
        ...(!isTarget
            ? [
                  { key: 'autoLanguageDetectionItemKey', text: localize(messages.autoDetectLanguageItem) },
                  { key: 'divider', text: '', itemType: DropdownMenuItemType.Divider }
              ]
            : []),
        ...supportedLanguages.map(l => ({ key: l.code, text: l.name })).sort((a, b) => (a.text < b.text ? -1 : 1))
    ];

    const fetchTranslatorResources = () =>
        dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
            toastData: {},
            id: subscriptionId,
            localStoreHouse: resourcesStoreHouse,
            filterPredicate: AzureResourcesStrategies.translatorResourceStrategy
        });

    const handleTargetLanguageChange = (option: IDropdownOption) => {
        if (isMultiTargetLanguageAvailable) {
            onTargetLanguageChange(
                option.selected ? [...targetLanguageKeys, option.key as string] : targetLanguageKeys.filter(t => t !== option.key)
            );
        } else {
            onTargetLanguageChange([option.key as string]);
        }
    };

    React.useEffect(() => {
        if (subscriptionId) {
            fetchTranslatorResources();
        }
    }, [subscriptionId]);

    React.useEffect(() => {
        if (storageAccount) {
            dispatcher.dispatch('checkStorageAccountRequiredRoles', {
                toastData: {},
                storageAccount,
                id: subscriptionId,
                tracker: storageAccountPermissionsTracker,
                roles: LuisConstants.Azure.StorageBlobDataContributorRoleAssignmentPermissions
            });
            dispatcher.dispatch('checkAndEnableStorageCORS', {
                tracker: corsCheckTracker,
                storageAccountName: storageAccount.name
            });
        }
    }, [selectedStorageAccount]);

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 15 }}>
                <LuisDropdown
                    required
                    styles={dropdownStyles}
                    selectedKey={csAccountStore?.id}
                    options={translatorResourcesOptions}
                    calloutProps={{ styles: calloutStyle }}
                    data-automation-id="azureResourceDropdown"
                    label={localize(messages.selectResourceLabel)}
                    errorMessage={isValidResource.secondaryErrorMessage}
                    placeholder={localize(messages.selectResourcePlaceholder)}
                    ariaLabel={localize(messages.selectResourcePlaceholder)}
                    isLoading={resourcesStoreHouse.tracker.isRunning || resourceTracker.isRunning}
                    onChange={(_, option) =>
                        dispatcher.dispatch('switchAzureCsAccountInSession', {
                            toastData: {},
                            tracker: resourceTracker,
                            csAccountStore: resourcesStoreHouse.items.find(r => r.id === option.key)
                        })
                    }
                    onRenderOption={p => (
                        <Stack horizontal verticalFill verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <LuisText color={demoTheme.defaultTextColor} fontWeight={p.itemType === DropdownMenuItemType.Header && 500}>
                                {p.text}
                            </LuisText>
                            {p.data && (
                                <SecondaryText variant="xSmall">
                                    ({(p.data as AzureCsAccountStore)?.region}, {(p.data as AzureCsAccountStore)?.sku.code})
                                </SecondaryText>
                            )}
                        </Stack>
                    )}
                />
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 15 }}>
                    <IconButton
                        onClick={fetchTranslatorResources}
                        iconProps={{ iconName: 'Refresh' }}
                        ariaLabel={'Refresh'}
                        data-automation-id="azureResourceRefreshBtn"
                    />
                    <ExternalLink styles={createButtonStyles} href="https://aka.ms/createTranlsationResourceAzure">
                        {localize(messages.createOne)}
                    </ExternalLink>
                </Stack>
            </Stack>

            {!csAccountStore?.isIdentityEnabled && (
                <CollapsedErrorMessage error={new Error(localize(messages.invalidResource))}>
                    <Link
                        onClick={() =>
                            dispatcher.dispatch('updateActiveGlobalBlade', {
                                newBlade: 'settings',
                                settingsTab: 'resources'
                            })
                        }>
                        {localize(messages.enableIdentity)}
                    </Link>
                </CollapsedErrorMessage>
            )}

            {storageAccountsError && (
                <CollapsedErrorMessage
                    error={storageAccountsError}
                    collapsedErrorMessage={
                        storageAccountsError?.name === LuisConstants.HttpResponseCodes.NotAuthorizedAction &&
                        localize(messages.invalidPermissionsStorageAccount)
                    }
                />
            )}

            {needStorageAccount &&
                [
                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 15 }}>
                        <LuisDropdown
                            required
                            styles={dropdownStyles}
                            options={storageAccountsOptions}
                            selectedKey={selectedStorageAccount}
                            isLoading={isStorageAccountsLoading}
                            calloutProps={{ styles: calloutStyle }}
                            data-automation-id="azureStorageAccountDropdown"
                            label={localize(messages.selectStorageAccountLabel)}
                            ariaLabel={localize(messages.selectStorageAccountPlaceholder)}
                            placeholder={localize(messages.selectStorageAccountPlaceholder)}
                            onChange={(_, option) => onStorageAccountChange(option.key as string)}
                            disabled={!isValidResource.isValid || !csAccountStore?.isIdentityEnabled || Boolean(storageAccountsError)}
                        />
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 15 }}>
                            <IconButton
                                onClick={refreshStorageAccounts}
                                iconProps={{ iconName: 'Refresh' }}
                                ariaLabel={'Refresh'}
                                disabled={!isValidResource.isValid}
                                data-automation-id="azureStorageAccountRefreshBtn"
                            />
                            <ExternalLink styles={createButtonStyles} href="https://aka.ms/createStorageResourceAzure">
                                {localize(messages.createOne)}
                            </ExternalLink>
                        </Stack>
                    </Stack>,
                    storageAccountPermissionsTracker.isFailed && (
                        <CollapsedErrorMessage error={new Error(localize(messages.invalidPermissionsStorageAccount))} />
                    ),
                    corsCheckTracker.isFailed && (
                        <CollapsedErrorMessage
                            error={corsCheckTracker.error}
                            collapsedErrorMessage={
                                corsCheckTracker.error?.name === LuisConstants.HttpResponseCodes.NotAuthorizedAction &&
                                localize(messages.enableCORSFailed, {
                                    storageAccountName: storageAccount.name
                                })
                            }
                        />
                    )
                ].filter(Boolean)}

            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10 }} styles={{ root: { flex: 1 } }}>
                    <LuisDropdown
                        required
                        orderByKey="none"
                        options={getLanguageOptions()}
                        styles={languageDropdownStyles}
                        selectedKey={sourceLanguageKey}
                        disabled={!isValidResource.isValid}
                        calloutProps={{ styles: calloutStyle }}
                        data-automation-id="sourceLanguageDropdown"
                        label={localize(messages.sourceLanguageLabel)}
                        placeholder={localize(messages.selectLanguagePlaceholder)}
                        ariaLabel={localize(messages.selectLanguagePlaceholder)}
                        onChange={(_, option) => onSourceLanguageChange(option.key as string)}
                    />
                    <Icon iconName="Switch" styles={{ root: { lineHeight: '32px' } }} />
                    <LuisDropdown
                        required
                        styles={languageDropdownStyles}
                        selectedKeys={targetLanguageKeys}
                        options={getLanguageOptions(true)}
                        disabled={!isValidResource.isValid}
                        calloutProps={{ styles: calloutStyle }}
                        data-automation-id="targetLanguageDropdown"
                        multiSelect={isMultiTargetLanguageAvailable}
                        label={localize(messages.targetLanguageLabel)}
                        placeholder={localize(messages.selectLanguagePlaceholder)}
                        ariaLabel={localize(messages.selectLanguagePlaceholder)}
                        onChange={(_, option) => handleTargetLanguageChange(option)}
                        selectedKey={!isMultiTargetLanguageAvailable && targetLanguageKeys[0]}
                    />
                </Stack>
                {showCategory && (
                    <LuisDropdown
                        selectedKey={selectedCategory}
                        styles={{ root: { flex: 1 } }}
                        disabled={!isValidResource.isValid}
                        data-automation-id="categoryDropdown"
                        calloutProps={{ styles: calloutStyle }}
                        label={localize(messages.selectCategoryLabel)}
                        options={categories.map(c => ({ key: c, text: c }))}
                        placeholder={localize(messages.selectCategoryPlaceholder)}
                        ariaLabel={localize(messages.selectCategoryPlaceholder)}
                        onChange={(_, option) => onCategoryChange(option.key as string)}
                    />
                )}
            </Stack>
        </Stack>
    );
});
